<script>
import DocumentReviewAgentRiskAnalysis from '@/V2/documents/document-view/document-review-agent/DocumentReviewAgentRiskAnalysis.vue';
import DocumentRightDataBox from '@/V2/documents/document-view/DocumentRightDataBox';
import DocumentReviewAgentRiskSummary from '@/V2/documents/document-view/document-review-agent/DocumentReviewRiskSummary.vue';
import DocumentSimilarDocuments from '@/V2/documents/document-view/DocumentSimilarDocuments.vue';
import DocumentReviewAgentNoRuns from '@/V2/documents/document-view/document-review-agent/DocumentReviewAgentNoRuns.vue';
import DocumentProcessingStatus from '@/V2/documents/document-view/DocumentProcessingStatus.vue';
import InfoIcon from '@/V2/components/InfoIcon';
import DocumentFontsDisplay from '@/V2/documents/document-view/signals/DocumentFontsDisplay';
import DocumentDetails from '@/V2/documents/document-view/DocumentDetails.vue';
import LoadingBoxes from '@/V2/components/LoadingBoxes';
import LegacyVerificationsGroup from '@/V2/documents/document-view/verifications/LegacyVerificationsGroup.vue';
import ChecksRequirementsGroup from '@/V2/documents/document-view/checks/ChecksRequirementsGroup.vue';
import VerificationsGroup from '@/V2/documents/document-view/verifications/VerificationsGroup.vue';
import SignalsGroup from '@/V2/documents/document-view/signals/SignalsGroup.vue';
import { mapActions, mapGetters } from 'vuex';
import { iAccordion, iAccordionItem } from '@inscribe/signal';
import { RUN_STATUS } from '@/constants/analyst-constants';
import { getLastRun } from '@/V2/documents/document-view/document-review-agent/utils';
import { PROCESSING } from '@/constants/documentStates';

export default {
  components: {
    ChecksRequirementsGroup,
    DocumentDetails,
    DocumentReviewAgentNoRuns,
    DocumentReviewAgentRiskAnalysis,
    DocumentRightDataBox,
    DocumentReviewAgentRiskSummary,
    DocumentSimilarDocuments,
    iAccordion,
    iAccordionItem,
    LoadingBoxes,
    InfoIcon,
    SignalsGroup,
    LegacyVerificationsGroup,
    DocumentFontsDisplay,
    VerificationsGroup,
    DocumentProcessingStatus,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
    customerId: {
      type: String,
      default: null,
    },
    currentOverlays: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      loadingRunsData: true,
      ANIMATION_TIME: 300,
      currentFullGroup: null,
      currentVerification: null,
      currentVerificationComponent: null,
      summarySubtitles: {},
      processingEventsPollingInterval: null,
    };
  },
  computed: {
    ...mapGetters(['getCachedDocumentReviewAgentRunsForDocId']),
    documentId() {
      return this.document?.id;
    },
    documentIsProcessing() {
      return this.document.state === PROCESSING;
    },
    runs() {
      return this.getCachedDocumentReviewAgentRunsForDocId(
        this.$route.params.document_id,
      );
    },
    runsArePresent() {
      return this.runs?.length > 0;
    },
    runIsBeingProcessed() {
      return (
        !!this.lastRun && [RUN_STATUS.PENDING].includes(this.lastRun.status)
      );
    },
    lastRun() {
      return getLastRun(this.runs);
    },
    documentRiskLevel() {
      // Currently the parent document data is not automatically refetched when
      // the agent finishes. So for now we'll fall back to the risk level value
      // from the latest agent run.
      return this.document.risk_level ?? this.lastRun?.output?.risk_level;
    },
    showLoadingState() {
      return this.loadingRunsData || this.runIsBeingProcessed;
    },
    hasVerifications() {
      return this.lastRun?.output?.verifications?.length > 0;
    },
  },
  watch: {
    // this is needed to adjust the overlay state in case the right is closed by the X button
    currentOverlays: {
      deep: true,
      immediate: true,
      handler: function (overlays) {
        const currentStateOpenCount = 0 + (this.currentVerification ? 1 : 0);
        // just consider closing
        if (currentStateOpenCount > overlays.length) {
          setTimeout(() => {
            if (overlays.length === 0) {
              this.currentVerification = null;
              this.currentVerificationComponent = null;
              setTimeout(() => {
                this.currentFullGroup = null;
              }, this.ANIMATION_TIME);
            } else if (overlays.length === 1) {
              this.currentVerification = null;
            }
          }, this.ANIMATION_TIME);
        }
      },
    },
    documentRiskLevel: {
      handler(newRiskLevel) {
        // When risk level is populated, stop polling processing events and
        // fetch final data state.
        if (newRiskLevel) {
          this.clearProcessingEventsPoll();
          this.fetchProcessingEvents();
        }
      },
    },
  },
  async mounted() {
    this.$store.dispatch('loadSimilarDocuments', {
      customerId: this.customerId,
      documentId: this.document.id,
    });

    if (this.documentRiskLevel) {
      // If risk level is populated, fetch all the processing events once
      this.fetchProcessingEvents();
    } else {
      // If risk level is not yet populated, start polling processing events
      this.startPollingProcessingEvents();
    }

    // Refetch run data on every mount to make sure we have the latest value.
    // If there's already a cached value in the store, it will be visible while
    // the new data loads in background.
    this.loadingRunsData = true;
    await this.fetchAndCacheDocumentReviewAgentRunsForDocId({
      documentId: this.$route.params.document_id,
      customerUUID: this.customerId,
    }).finally(() => {
      this.loadingRunsData = false;
    });
  },
  beforeUnmount() {
    this.clearProcessingEventsPoll();
  },
  methods: {
    ...mapActions([
      'fetchAndCacheDocumentReviewAgentRunsForDocId',
      'fetchAndCacheProcessingEventsForDocumentId',
    ]),
    async fetchProcessingEvents() {
      if (!this.document.id) {
        return;
      }
      try {
        await this.fetchAndCacheProcessingEventsForDocumentId({
          documentId: this.document.id,
        });
      } catch {
        // Stop polling if there's an error
        this.clearProcessingEventsPoll();
      }
    },
    startPollingProcessingEvents() {
      // Clean up any existing polling interval
      this.clearProcessingEventsPoll();
      // If we have a doc ID, do an initial fetch and set up polling interval
      this.fetchProcessingEvents();
      this.processingEventsPollingInterval = setInterval(
        this.fetchProcessingEvents,
        3000,
      );
    },
    clearProcessingEventsPoll() {
      if (this.processingEventsPollingInterval) {
        clearInterval(this.processingEventsPollingInterval);
        this.processingEventsPollingInterval = null;
      }
    },
    setVerification(verification) {
      this.currentVerification = verification;
    },
    setVerificationComponent(component) {
      this.currentVerificationComponent = component;
    },
    showItemDetails(verification) {
      this.currentVerification = verification;
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs.documentReviewAgentDetails);
      });
    },
    openDecisionEngine(routeName) {
      this.$router.push({ name: routeName || 'decision-engine-detectors' });
    },
    onShowPanel(data) {
      this.currentVerificationComponent = data.overlay;
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs[data.ref]);
      });
    },
    openRiskSummary() {
      this.currentVerificationComponent = 'risk-summary';
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs.documentRiskSummary);
      });
    },
    openTrustScoreDetails() {
      this.currentVerificationComponent = 'similar-documents';
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs.similarDocuments);
      });
    },
    openSummaryPage(group) {
      if (group === 'trustScore') {
        this.openTrustScoreDetails();
      } else {
        this.currentFullGroup = null;
        this.$nextTick(() => {
          this.currentFullGroup = group;
        });
      }
    },
  },
};
</script>

<template>
  <div
    class="document-with-analyst d-flex flex-column"
    data-testid="document-right-with-analyst"
  >
    <!-- document details -->
    <div>
      <i-accordion :allow-multi-open="true">
        <!-- Status during initial document processing -->
        <document-processing-status
          v-if="documentIsProcessing"
          :document="document"
        ></document-processing-status>

        <!-- Initial processing finished, but no agent runs present -->
        <document-review-agent-no-runs
          v-else-if="!runsArePresent && !loadingRunsData"
          :document="document"
          @open-trust-score-details="openTrustScoreDetails"
        >
        </document-review-agent-no-runs>

        <!-- Risk analysis including agent runs -->
        <document-review-agent-risk-analysis
          v-else
          :runs="runs"
          :document="document"
          :is-loading="loadingRunsData"
          @open-risk-analysis-details="openRiskSummary"
        ></document-review-agent-risk-analysis>

        <signals-group
          :document="document"
          :current-full-group="currentFullGroup"
          :current-verification="currentVerification"
          :current-verification-component="currentVerificationComponent"
          :show-loading-state="showLoadingState"
          @open-decision-engine="openDecisionEngine"
          @set-verification="setVerification"
          @set-verification-component="setVerificationComponent"
          @open-overlay="$emit('open-overlay', $event)"
          @update:subtitle="summarySubtitles.signals = $event"
        ></signals-group>

        <!-- dynamic groups-->
        <loading-boxes
          v-if="showLoadingState"
          class="accordion-content i-mx-10 i-mt-10"
        ></loading-boxes>

        <verifications-group
          :document="document"
          :last-run="lastRun"
          :current-full-group="currentFullGroup"
          :current-verification="currentVerification"
          :current-verification-component="currentVerificationComponent"
          :show-loading-state="showLoadingState"
          @open-overlay="$emit('open-overlay', $event)"
          @set-verification="setVerification"
          @update:subtitle="summarySubtitles.verifications = $event"
        ></verifications-group>

        <!-- check requirements -->
        <checks-requirements-group
          :document="document"
          :last-run="lastRun"
          :current-full-group="currentFullGroup"
          :current-verification="currentVerification"
          :current-verification-component="currentVerificationComponent"
          :show-loading-state="showLoadingState"
          @show-item-details="showItemDetails"
          @open-overlay="$emit('open-overlay', $event)"
          @set-verification="setVerification"
          @open-decision-engine="openDecisionEngine"
          @update:subtitle="summarySubtitles.requirements = $event"
        ></checks-requirements-group>

        <!-- legacy verifications -->
        <legacy-verifications-group
          :current-full-group="currentFullGroup"
          :current-verification="currentVerification"
          :current-verification-component="currentVerificationComponent"
          :document="document"
          @set-verification="setVerification"
          @set-verification-component="setVerificationComponent"
          @show-item-details="showItemDetails"
          @open-overlay="$emit('open-overlay', $event)"
        ></legacy-verifications-group>
      </i-accordion>
    </div>

    <!-- overlay options -->

    <!-- risk summary -->
    <document-review-agent-risk-summary
      v-if="
        runsArePresent &&
        (['risk-summary', 'similar-documents', 'metadata'].includes(
          currentVerificationComponent,
        ) ||
          currentVerification?.detailsType === 'fraud')
      "
      ref="documentRiskSummary"
      :subtitles="summarySubtitles"
      :document="document"
      :has-verifications="hasVerifications"
      :last-run="lastRun"
      @open-risk-summary="openSummaryPage"
    ></document-review-agent-risk-summary>

    <!-- trust score details -->
    <document-similar-documents
      v-if="currentVerificationComponent === 'similar-documents'"
      ref="similarDocuments"
      :customer-id="customerId"
      :document="document"
    ></document-similar-documents>

    <!-- fonts display opene from nav panel -->
    <document-fonts-display
      v-if="currentVerificationComponent === 'fonts-display'"
      ref="documentFontsDisplay"
      :document="document"
    ></document-fonts-display>

    <document-details
      v-if="currentVerificationComponent === 'document-details'"
      ref="documentDetails"
      :document="document"
    ></document-details>
  </div>
</template>

<style lang="scss" scoped>
.document-with-analyst {
  width: 100%;
  overflow: auto;

  :deep(.signal-button) {
    .subtitle {
      max-width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}
</style>
